import { FileUploader } from "@/lib/FileUpload/FileUploader";
import { Button } from "@/components/catalyst/button";
import { TrashIcon } from "lucide-react";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  createMediaMutation,
  deleteMediaMutation,
} from "@/app/Media/GraphQL/mediaMutations";
import { MediaCollection, ModelType } from "@/gql/graphql";
import { supplierPurchasableExtra } from "@/app/Suppliers/GraphQL/supplierPurchasableExtraList";

export function SupplierPurchasableExtraImagePanel({
  extraId,
}: {
  extraId: string;
}) {
  const { mutateAsync: createMedia, isPending: isAttaching } =
    useGqlMutation(createMediaMutation);
  const { mutateAsync: deleteMedia, isPending: isDeleting } =
    useGqlMutation(deleteMediaMutation);

  // Read the image from its own query rather than the record passed into the
  // dialog, so an upload's cache invalidation shows the new image immediately.
  const { data } = useGqlQuery(supplierPurchasableExtra, { id: extraId });
  const image = data?.supplierPurchasableExtra?.image;

  return (
    <div className="col-span-full space-y-3">
      <div className="text-sm font-medium text-gray-900">Image</div>
      <p className="text-sm text-gray-500">
        Shown to customers when they choose this extra at checkout.
      </p>

      {image ? (
        <div className="relative inline-block">
          <img
            src={image.url}
            alt={image.name}
            className="h-32 w-auto rounded-md border border-zinc-200 object-cover"
          />
          <div className="absolute right-2 top-2">
            <Button
              color="red"
              type="button"
              aria-label="Remove image"
              isLoading={isDeleting}
              LeadingIcon={TrashIcon}
              onClick={async () => {
                await deleteMedia({ id: image.id });
              }}
            />
          </div>
        </div>
      ) : null}

      <FileUploader
        isDisabled={isAttaching || isDeleting}
        maxFileCount={1}
        onUpload={async (uploads) => {
          const uploaded = uploads[0];
          if (!uploaded) return;
          if (image) {
            await deleteMedia({ id: image.id });
          }
          await createMedia({
            input: {
              file_name: uploaded.file.name,
              objectId: extraId,
              objectType: ModelType.SupplierPurchasableExtra,
              collectionName: MediaCollection.PurchasableExtraImage,
              key: uploaded.filePath,
            },
          });
        }}
      />
    </div>
  );
}
